import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    CurrencyDollarIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    CalendarDaysIcon,
    ChartBarIcon,
    StarIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getRevenueAnalytics, getHourlyRevenue } from "../api/analyticsApi";
import type { AnalyticsDateRange } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtCurrency = (amount: number | null | undefined) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
    }).format(amount);
};

const fmtNumber = (n: number | null | undefined) => {
    if (n == null) return "—";
    return new Intl.NumberFormat("en-NG").format(n);
};

// ============================================================================
// Component
// ============================================================================

export const RevenueAnalyticsRoute = () => {
    const [dateRange, setDateRange] = useState<AnalyticsDateRange>({});
    const [hourlyDate, setHourlyDate] = useState("");

    const {
        data: revenueData,
        isLoading: revenueLoading,
        error: revenueError,
        refetch: refetchRevenue,
        isFetching: revenueFetching,
    } = useQuery({
        queryKey: ["analytics-revenue", dateRange],
        queryFn: () => getRevenueAnalytics(dateRange),
    });

    const {
        data: hourlyData,
        isLoading: hourlyLoading,
        error: hourlyError,
    } = useQuery({
        queryKey: ["analytics-revenue-hourly", hourlyDate],
        queryFn: () => getHourlyRevenue(hourlyDate ? { date: hourlyDate } : undefined),
    });

    const rev = revenueData?.data;
    const hourly = hourlyData?.data;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (revenueLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (revenueError) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">
                    Failed to load revenue analytics
                </h3>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">
                    {(revenueError as Error).message}
                </p>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Date Range Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={dateRange.startDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, startDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate ?? ""}
                        onChange={(e) =>
                            setDateRange((p) => ({ ...p, endDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => refetchRevenue()}
                    disabled={revenueFetching}
                >
                    <ArrowPathIcon
                        className={`mr-1.5 h-4 w-4 ${revenueFetching ? "animate-spin" : ""}`}
                    />
                    Refresh
                </Button>
            </div>

            {/* Summary Cards */}
            {rev?.summary && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-center gap-3">
                            <div className="rounded-xl bg-emerald-50 p-2 dark:bg-emerald-900/20">
                                <CurrencyDollarIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Total Revenue
                                </p>
                                <p className="text-xl font-bold text-slate-900 dark:text-white">
                                    {fmtCurrency(rev.summary.totalRevenue)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                            <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${rev.summary.weekOverWeekGrowth >= 0
                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                        : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                                    }`}
                            >
                                {rev.summary.weekOverWeekGrowth >= 0 ? (
                                    <ArrowTrendingUpIcon className="h-3 w-3" />
                                ) : (
                                    <ArrowTrendingDownIcon className="h-3 w-3" />
                                )}
                                {Math.abs(rev.summary.weekOverWeekGrowth).toFixed(1)}%
                            </span>
                            <span className="text-xs text-slate-400">WoW</span>
                        </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Platform Commission
                        </p>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                            {fmtCurrency(rev.summary.platformCommission)}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                            Driver Earnings: {fmtCurrency(rev.summary.driverEarnings)}
                        </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Avg Fare / Total Trips
                        </p>
                        <p className="text-xl font-bold text-slate-900 dark:text-white">
                            {fmtCurrency(rev.summary.averageFare)}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                            {fmtNumber(rev.summary.totalTrips)} trips
                        </p>
                    </div>
                </div>
            )}

            {/* Revenue by Payment Method */}
            {rev?.revenueByPaymentMethod && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Revenue by Payment Method
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-3">
                        {Object.entries(rev.revenueByPaymentMethod).map(([method, amount]) => (
                            <div
                                key={method}
                                className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50"
                            >
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {method}
                                </p>
                                <p className="text-lg font-bold text-slate-900 dark:text-white">
                                    {fmtCurrency(amount)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Daily Breakdown */}
            {rev?.dailyBreakdown && rev.dailyBreakdown.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ChartBarIcon className="h-4 w-4" /> Daily Revenue Breakdown
                    </h3>

                    {/* Mini bar chart */}
                    <div className="mb-4 flex items-end gap-1" style={{ height: 120 }}>
                        {rev.dailyBreakdown.map((day) => {
                            const max = Math.max(
                                ...rev.dailyBreakdown.map((d) => d.revenue),
                                1
                            );
                            const pct = (day.revenue / max) * 100;
                            return (
                                <div
                                    key={day.date}
                                    className="group relative flex-1"
                                    title={`${day.date}: ${fmtCurrency(day.revenue)} (${day.trips} trips)`}
                                >
                                    <div
                                        className="w-full rounded-t bg-gradient-to-t from-emerald-600 to-emerald-400 transition-all group-hover:from-emerald-700 group-hover:to-emerald-500"
                                        style={{
                                            height: `${pct}%`,
                                            minHeight: day.revenue ? 4 : 0,
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Table */}
                    <div className="max-h-64 overflow-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Date
                                    </th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Revenue
                                    </th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Trips
                                    </th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Commission
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {rev.dailyBreakdown.map((day) => (
                                    <tr key={day.date}>
                                        <td className="px-3 py-2 text-slate-900 dark:text-white">
                                            {day.date}
                                        </td>
                                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">
                                            {fmtCurrency(day.revenue)}
                                        </td>
                                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                                            {fmtNumber(day.trips)}
                                        </td>
                                        <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400">
                                            {fmtCurrency(day.commission)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Top Earning Drivers */}
            {rev?.topEarningDrivers && rev.topEarningDrivers.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <StarIcon className="h-4 w-4" /> Top Earning Drivers
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Driver
                                    </th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Earnings
                                    </th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Trips
                                    </th>
                                    <th className="px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                                        Rating
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {rev.topEarningDrivers.map((driver) => (
                                    <tr key={driver.driverId}>
                                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">
                                            {driver.driverName}
                                        </td>
                                        <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400">
                                            {fmtCurrency(driver.totalEarnings)}
                                        </td>
                                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                                            {fmtNumber(driver.tripCount)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                                <StarIcon className="h-3.5 w-3.5 fill-current" />
                                                {driver.averageRating?.toFixed(1)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Hourly Revenue */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ChartBarIcon className="h-4 w-4" /> Hourly Revenue
                    </h3>
                    <input
                        type="date"
                        value={hourlyDate}
                        onChange={(e) => setHourlyDate(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>

                {hourlyLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
                    </div>
                ) : hourlyError ? (
                    <p className="text-sm text-rose-500">Failed to load hourly data</p>
                ) : hourly?.hourlyRevenue && hourly.hourlyRevenue.length > 0 ? (
                    <>
                        <div className="mb-2 flex items-center gap-4 text-xs text-slate-400">
                            <span>
                                Peak: Hour {hourly.peakHour} ({fmtCurrency(hourly.peakRevenue)})
                            </span>
                            <span>Total: {fmtCurrency(hourly.totalRevenue)}</span>
                        </div>
                        <div className="flex items-end gap-1" style={{ height: 100 }}>
                            {hourly.hourlyRevenue.map((h) => {
                                const max = Math.max(
                                    ...hourly.hourlyRevenue.map((x) => x.revenue),
                                    1
                                );
                                const pct = (h.revenue / max) * 100;
                                const isPeak = h.hour === hourly.peakHour;
                                return (
                                    <div
                                        key={h.hour}
                                        className="group relative flex-1"
                                        title={`${h.hour}:00 — ${fmtCurrency(h.revenue)} (${h.trips} trips)`}
                                    >
                                        <div
                                            className={`w-full rounded-t transition-all ${isPeak
                                                    ? "bg-amber-500"
                                                    : "bg-gradient-to-t from-primary-600 to-primary-400 group-hover:from-primary-700 group-hover:to-primary-500"
                                                }`}
                                            style={{
                                                height: `${pct}%`,
                                                minHeight: h.revenue ? 4 : 0,
                                            }}
                                        />
                                        {h.hour % 4 === 0 && (
                                            <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] text-slate-400">
                                                {h.hour}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-5 text-center text-[10px] text-slate-400">
                            Hour of day
                        </div>
                    </>
                ) : (
                    <p className="py-6 text-center text-sm text-slate-400">
                        No hourly data available
                    </p>
                )}
            </div>

            {/* Date range */}
            {rev?.dateRange && (
                <p className="text-right text-xs text-slate-400 dark:text-slate-500">
                    Period: {rev.dateRange.startDate} — {rev.dateRange.endDate}
                </p>
            )}
        </div>
    );
};
